/**
 * Scene-sprite checks (R32/D111) — the D96 contract's loader half: every
 * binding resolves both directions, every promise holds where it is shown,
 * and the silhouette rule's schema-checkable shadow is enforced.
 *
 * @remarks
 * The D89 treatment, sprite-side:
 *
 * - Bindings resolve BOTH directions: a room's `sprites` entries exist in
 *   the table, and a declared sprite is bound by some room (dead content
 *   otherwise — the D31/D47 law).
 * - A room binding sprites must HAVE a plate — a sprite with no ground has
 *   nothing to composite onto.
 * - `depicts`, when declared, is the D89 promise per binding room: the item
 *   referable (placed or ambient) wherever the sprite shows — or, for an
 *   actor (D236), the body present wherever it is drawn, which only the
 *   sprite's own `visibleWhen` can say (`sprite-depiction.ts`).
 * - `visibleWhen` joins the condition walk (reference integrity) and the
 *   constant-condition check (a sprite visible under a constant-false
 *   condition is dead; constant-true means the paint belongs in the plate).
 * - D255: a `transition` plays from a sprite the same room binds, and not
 *   from the sprite itself — otherwise there is no change for it to play on.
 * - **The shared-placement rule** (D110's silhouette rule, its
 *   schema-checkable shadow): two sprites depicting the SAME item or actor
 *   bound in the SAME room are variants of one thing, and a lighting change
 *   is a change of light, never of position — nor is a body's fall a change
 *   of place — so their placements must be identical. Keyed by the id alone,
 *   because content ids are one space (D232).
 *
 * @packageDocumentation
 */

import { type ActorDefinition, type ContentPackDefinition, roomsAnActorCanStandIn, type SpriteDefinition, stageDirectionsOf } from '@orpheus/content-schema';

import { findConstantBinaryStateCondition } from './authoring-reachability.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkCondition, declaredIdSetsOf } from './reference-walkers.js';
import { brokenReference, collectDuplicates } from './semantic-validation.js';
import { bodyDepictionFailures } from './sprite-depiction.js';

/**
 * Run every sprite check against a schema-valid pack.
 *
 * @param contentPack - The parsed pack
 * @returns Every sprite failure found (empty = the scene layer is sound)
 */
export const validateSpriteAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const itemsById = new Map(contentPack.items.map((item) => [item.itemId, item]));
	const actorsById = new Map<string, ActorDefinition>(contentPack.actors.map((actor) => [actor.actorId, actor]));
	const stage = stageDirectionsOf(contentPack);
	const declared = declaredIdSetsOf(contentPack);
	const spritesById = new Map<string, SpriteDefinition>(contentPack.sprites.map((sprite) => [sprite.spriteId, sprite]));
	const boundSpriteIds = new Set<string>();

	collectDuplicates(
		contentPack.sprites.map((sprite, spriteIndex) => ({
			declarationPath: `sprites[${String(spriteIndex)}].spriteId`,
			declaredId: sprite.spriteId,
		})),
		'sprite',
		failures,
	);

	contentPack.sprites.forEach((sprite, spriteIndex) => {
		const spritePath = `sprites[${String(spriteIndex)}]`;

		checkCondition(sprite.visibleWhen, `${spritePath}.visibleWhen`, declared, failures);

		const constantCondition = findConstantBinaryStateCondition(sprite.visibleWhen, itemsById);

		if (constantCondition !== undefined) {
			failures.push(
				brokenReference(
					`${spritePath}.visibleWhen`,
					sprite.spriteId,
					constantCondition.type === 'switched_on'
						? 'that thing declares no switch, so this sprite can never appear'
						: 'that thing declares no door, so this sprite is always visible — unconditional paint belongs in the plate',
				),
			);
		}

		if (sprite.depicts !== undefined && !declared.itemIds.has(sprite.depicts) && !declared.actorIds.has(sprite.depicts)) {
			failures.push(brokenReference(`${spritePath}.depicts`, sprite.depicts, 'the sprite depicts an undeclared item or actor'));
		}
	});

	const ambientItemIds = new Set<string>(contentPack.ambientItemIds);

	contentPack.rooms.forEach((room, roomIndex) => {
		if (room.sprites.length === 0) {
			return;
		}

		const roomPath = `rooms[${String(roomIndex)}]`;

		// A sprite with no ground: the compositor stacks over the room's plate,
		// and a plate-less room has nothing to stack onto.
		if (room.image === undefined) {
			failures.push(
				brokenReference(`${roomPath}.sprites`, room.roomId, 'this room binds sprites but names no plate — a sprite has nothing to composite onto'),
			);
		}

		const placedItemIds = new Set<string>(room.itemPlacements);
		const placementsByDepictedId = new Map<string, SpriteDefinition>();
		const seenBindings = new Set<string>();

		room.sprites.forEach((spriteId, bindingIndex) => {
			const bindingPath = `${roomPath}.sprites[${String(bindingIndex)}]`;

			if (seenBindings.has(spriteId)) {
				failures.push(brokenReference(bindingPath, spriteId, 'duplicate sprite binding — one sprite stacks once'));
			}
			seenBindings.add(spriteId);
			boundSpriteIds.add(spriteId);

			const sprite = spritesById.get(spriteId);

			if (sprite === undefined) {
				failures.push(brokenReference(bindingPath, spriteId, 'the room binds a sprite the sprites table does not declare'));

				return;
			}

			// D255: a transition plays where its sprite replaces another on screen,
			// so the other must be a different sprite this room shows too.
			const fromSpriteId = sprite.transition?.fromSpriteId;

			if (fromSpriteId !== undefined && (fromSpriteId === spriteId || !room.sprites.includes(fromSpriteId))) {
				failures.push(
					brokenReference(
						bindingPath,
						fromSpriteId,
						`the transition into "${spriteId}" plays from "${fromSpriteId}", which this room does not show as another sprite — it could never play here`,
					),
				);
			}

			if (sprite.depicts === undefined) {
				return;
			}

			const { depicts } = sprite;
			const depictedActor = actorsById.get(depicts);

			// The D89 promise, sprite-side: what the composite shows must be
			// referable where it is shown — a thing by where it is placed, a
			// body (D236) by the sprite's own condition.
			if (depictedActor !== undefined) {
				failures.push(
					...bodyDepictionFailures(sprite, depictedActor, {
						bindingPath,
						roomId: room.roomId,
						standableRoomIds: roomsAnActorCanStandIn(depictedActor, stage),
					}),
				);
			} else if (declared.itemIds.has(depicts) && !placedItemIds.has(depicts) && !ambientItemIds.has(depicts)) {
				failures.push(
					brokenReference(
						bindingPath,
						depicts,
						`the sprite "${spriteId}" depicts "${depicts}", which is neither placed in this room nor ambient — a composite must not promise what the room cannot answer`,
					),
				);
			}

			// D110's silhouette rule, its schema-checkable shadow: variants of
			// one thing — a lighting change, a body alive and fallen — share
			// one placement.
			const siblingSprite = placementsByDepictedId.get(depicts);

			if (siblingSprite !== undefined && JSON.stringify(siblingSprite.placement) !== JSON.stringify(sprite.placement)) {
				failures.push(
					brokenReference(
						bindingPath,
						spriteId,
						`"${spriteId}" and "${siblingSprite.spriteId}" both depict "${depicts}" in this room with DIFFERENT placements — a lighting change is a change of light, never of position, and a body's fall is a change of pose, never of place (the D110 silhouette rule)`,
					),
				);
			}
			placementsByDepictedId.set(depicts, sprite);
		});
	});

	// The dead-content law, sprite-side: a declared sprite no room ever binds.
	contentPack.sprites.forEach((sprite, spriteIndex) => {
		if (!boundSpriteIds.has(sprite.spriteId)) {
			failures.push(
				brokenReference(`sprites[${String(spriteIndex)}].spriteId`, sprite.spriteId, 'no room binds this sprite, so it can never be composited'),
			);
		}
	});

	return failures;
};
